/**
 * Typed search params — the reason TanStack Router was chosen (PRD D2, §5 I9).
 *
 * ── The defect this file exists to make impossible ──
 *
 * Query params in this app are a SHELL-WIDE NAMESPACE, not a per-page one. The
 * root chrome reads `?q=` (the dock owns the search query; the view mirrors it),
 * and the peek hosts read `?peek=` / `?task=` on EVERY route. A page that
 * introduces its own `?task=` does not get its own variable — it silently drives
 * the object peek instead. That has already happened here once, and an HTTP 200
 * is not evidence against it, because the collision renders fine and does the
 * wrong thing.
 *
 * Under the App Router the only guard was "grep before you add a param", which
 * is a discipline, not a check. Here it is a TYPE:
 *
 *   - the root route validates `shellSearchSchema`, so every route's `useSearch()`
 *     is typed with the shell-wide params and no route has to re-declare them;
 *   - a route declares its OWN params through `routeSearchSchema({...})`, which
 *     REFUSES TO COMPILE if the shape re-declares a shell-wide key.
 *
 * Adding a shell-wide param is therefore a deliberate act (extend the schema
 * below, once) and stealing one is a build failure.
 */

import { z } from "zod";

/**
 * The params the root shell itself consumes on every route.
 *
 * This list is CENSUSED, not guessed — a key earns a place here only when root
 * chrome reads (or writes) it on an arbitrary route. The file:line evidence for
 * each is in the comments below, so the next person can re-check rather than
 * re-derive. A merely-popular param stays route-local: `range` (13 files) and
 * `tab` (3 files) have ZERO chrome readers, and `tab`'s three readers each cast
 * it to a DIFFERENT local union — reserving it at root would be wrong for all
 * three, and would make the shell a liar about who owns the name.
 */
const shellSearchShape = {
  /**
   * Dock search query. No chrome file READS it, but the dock WRITES it onto
   * whatever route is active (`_chrome/dock-bar.tsx`), and ~14 views mirror it
   * with the same meaning — CLAUDE.md rule 7. Reserved to make the contract
   * explicit instead of conventional.
   */
  q: z.string().optional(),
  /**
   * Object peek target. `PEEK_PARAM` in `packages/ui/src/blocks/object-link.tsx`
   * ("one per app"), read by `ObjectPeekHost` — mounted once in the root layout.
   */
  peek: z.string().optional(),
  /** Contextual Excellent pane. The value is explicit so malformed state fails closed. */
  companion: z.enum(["open"]).optional(),
  /** Visible pane on collapsed shells. URL ownership makes history and copied links reproducible. */
  pane: z.enum(["page", "companion", "inspector"]).optional(),
  /**
   * Task peek target — the legacy `?task=<id>` doorway read by `TaskPeekHost`
   * (also mounted once in the root layout), which resolves the integer id to a
   * ULID and REWRITES itself into `?peek=`. This is the collision that bit this
   * repo: a page that adds its own `?task=` does not get a variable, it drives
   * the peek host and then watches the shell rewrite its URL.
   */
  task: z.coerce.number().int().positive().optional(),
  /** Mode area. Read by `client-layout` (nav identity key), the dock's scope resolver, and the breadcrumb. */
  area: z.string().optional(),
  /** Preserved deep sub-route. Same three chrome readers as `area`. */
  sub: z.string().optional(),
  /** Mode view. Chrome reads it, and the root layout's mode resolver branches on `?view=review`. */
  view: z.string().optional(),
  /** Back-link provenance (`NAV_FROM_PARAM`) — read by the mode rail and the breadcrumb. */
  from: z.string().optional(),
  /** One-shot desktop deep-link (`/?dock=terminal`), consumed and stripped by the shell. */
  dock: z.string().optional(),
  /**
   * Live session URN (`live_session:<id>`). SI-2-01 / PRD §10.1 — shell-wide so
   * a dock, deep link, or any route can address the same attach target without
   * colliding with route-local params. Constant: `SESSION_PARAM` in
   * `@excellent/ui/session` (`packages/ui/src/session/params.ts`). Not
   * webauthn auth sessions (table `sessions`); live sessions only.
   */
  session: z.string().optional(),
  /**
   * Play deep-link trigger. ?play=1 opens the Play stage on mount;
   * consumed and stripped by AppPlayLaunchProvider so a reload won't re-fire.
   * Root-shell read, not per-route.
   */
  play: z.string().optional(),
  /**
   * Play start action key. ?start=<key> focuses Play on a specific card
   * (e.g. question-set:1 for the Wave-3 question set) instead of the ranked top.
   * Read alongside `play` by AppPlayLaunchProvider.
   */
  start: z.string().optional(),
  /**
   * Verification runtime scope. EnvProvider and VerificationShell read `?env=`
   * on every product route (`urlOverrideFromSearch` / `location.searchStr`).
   * Census: root chrome reader, therefore shell-wide. `/welcome` must inherit
   * this rather than claiming `env` as a route-local param.
   */
  env: z.string().optional(),
} satisfies z.ZodRawShape;

type RawShellSearch = Record<string, unknown>;

function pageOnlySearch(value: RawShellSearch): RawShellSearch {
  const next: RawShellSearch = { ...value, pane: "page" };
  delete next.companion;
  delete next.peek;
  delete next.task;
  return next;
}

function failClosedPaneSearch(value: unknown): unknown {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value;
  const search = value as RawShellSearch;
  const pane = search.pane;
  const companion = search.companion;
  const peek = search.peek;
  const validPane = pane === undefined || pane === "page" || pane === "companion" || pane === "inspector";
  const validCompanion = companion === undefined || companion === "open";
  const validPeek = peek === undefined || (typeof peek === "string" && peek.trim().length > 0);
  const hasInspectorTarget = validPeek && typeof peek === "string";
  const taskNumber = typeof search.task === "number"
    ? search.task
    : typeof search.task === "string" && search.task.trim()
      ? Number(search.task)
      : Number.NaN;
  const hasLegacyTaskTarget = Number.isInteger(taskNumber) && taskNumber > 0;

  if (
    !validPane
    || !validCompanion
    || !validPeek
    || (pane === "companion" && companion !== "open")
    || (pane === "inspector" && !hasInspectorTarget && !hasLegacyTaskTarget)
  ) {
    return pageOnlySearch(search);
  }

  return search;
}

/** Parse supporting panes as one coupled state so malformed deep links cannot open partial UI. */
export const shellSearchSchema = z.preprocess(
  failClosedPaneSearch,
  z.object(shellSearchShape),
);

/**
 * DELIBERATELY LEFT OUT, so the omission reads as a decision and not an
 * oversight: the Pulse drill params `g` / `t` / `c`. Chrome does read them
 * (`modes/nav-trail.ts`, `_chrome/mode-rail.tsx`), but `_chrome/client-layout.tsx`
 * explicitly EXCLUDES them from the navigation identity key. They are therefore
 * shell-read yet not shell-identifying, and reserving them would freeze a
 * question nobody has answered. Escalate to L4 with the rail, don't settle here.
 */
export type ShellSearch = z.infer<typeof shellSearchSchema>;
export type ShellSearchKey = keyof ShellSearch;

/** Runtime twin of `ShellSearchKey`, for non-type-land assertions and gates. */
export const SHELL_SEARCH_KEYS = Object.keys(shellSearchShape) as ShellSearchKey[];

declare const COLLISION: unique symbol;
/**
 * The type a colliding key is forced to have. No zod schema is assignable to it,
 * so the error surfaces ON the offending property with this name in the message.
 */
type ERROR_param_is_shell_wide_and_already_claimed = { readonly [COLLISION]: never };

type CollidingKeys<Shape> = Extract<keyof Shape, ShellSearchKey>;

/**
 * `unknown` (a no-op in an intersection) when the shape is clean; otherwise a
 * requirement that each colliding key be the unsatisfiable type above.
 */
type NoShellCollision<Shape> = [CollidingKeys<Shape>] extends [never]
  ? unknown
  : { [K in CollidingKeys<Shape>]: ERROR_param_is_shell_wide_and_already_claimed };

/**
 * Declare a route's own search params.
 *
 *   const search = routeSearchSchema({ subject: z.string().optional() });   // ok
 *   const search = routeSearchSchema({ task: z.string().optional() });      // COMPILE ERROR
 *
 * The second form fails with "Type 'ZodOptional<ZodString>' is not assignable to
 * type 'ERROR_param_is_shell_wide_and_already_claimed'" pointing at `task`.
 */
export function routeSearchSchema<Shape extends z.ZodRawShape>(
  shape: Shape & NoShellCollision<Shape>,
): z.ZodObject<Shape> {
  return z.object(shape as Shape);
}

/**
 * The merged view a route handler sees: shell-wide params plus its own. Routes
 * get this for free from TanStack's inherited-search typing; the alias exists so
 * a component prop can name the shape without re-deriving it.
 */
export type WithShellSearch<T> = ShellSearch & T;
